import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Announcer } from '@/components/ui/announcer';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './lib/contexts/AuthContext';
import ProtectedRoute from './components/features/auth/ProtectedRoute';
import RequireSuperuser from './components/features/auth/RequireSuperuser';
import RequireOwner from './components/features/auth/RequireOwner';
import MainLayout from './components/layout/MainLayout';
import PageLoadingFallback from './components/ui/PageLoadingFallback';

// Lazy load all page components for route-based code splitting
// Critical path pages (auth flow)
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupRouter = lazy(() => import('./pages/SignupRouter'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Main app pages
const DocumentLibraryPage = lazy(() => import('./pages/DocumentLibraryPage'));
const DocumentDetailPage = lazy(() => import('./pages/DocumentDetailPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
// Unified cross-plugin Dashboards page — replaced StartPage (T-20260915-2)
const DashboardsPage = lazy(() => import('./pages/DashboardsPage'));
// Note: ContentDraftPage removed - now redirects to /plugins/marketing/item/:id (PU-7.15)
// Note: CampaignsPage removed - now redirects to /plugins/marketing/library (PU-6.8)
const CSVImportPage = lazy(() => import('./pages/CSVImportPage'));
// Note: ContentPerformancePage + MonthlyReviewPage deleted (T-20260919-7a) —
// /performance and /monthly-reviews now redirect to /dashboards
// Story 24.8 — `/settings` is the role-open Settings hub; the team page
// (SettingsPage) moved to `/settings/team` behind <RequireOwner>.
const SettingsHubPage = lazy(() => import('./pages/SettingsHubPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ScheduledTasksSettingsPage = lazy(() => import('./pages/ScheduledTasksSettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/IntegrationsSettingsPage'));
// T-20260916-8 — the `ama_` machine-key surface (owner-only).
const ApiKeysSettingsPage = lazy(() => import('./pages/ApiKeysSettingsPage'));
// Story 29.3 — who gets notified (role-open; read-only for non-owners).
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
// Plugin settings under Admin, with a plugin switcher (T-20260915-3)
const PluginSettingsHostPage = lazy(() => import('./pages/PluginSettingsHostPage'));

// Plugin pages
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));

// In-app documentation (owner-facing guide, build-time bundled)
const DocsPage = lazy(() => import('./pages/DocsPage'));
// Story 24.7 — the phone More tab and the superuser Engineering list page.
const MorePage = lazy(() => import('./pages/MorePage'));
const EngineeringPage = lazy(() => import('./pages/EngineeringPage'));

// Unified chat shell (Story 18.7)
const ChatPage = lazy(() => import('./pages/ChatPage'));

// Dedicated task surface (Epic 6 story 6.1 — sprint-change-proposal-2026-08-25)
const TasksPage = lazy(() => import('./pages/TasksPage'));
// Dedicated review surfaces, registered per task_type (ADR-022 S4, T-20260914-11c)
const TaskReviewPage = lazy(() => import('./pages/TaskReviewPage'));
const PluginChatToUnifiedShellRedirect = lazy(() =>
  import('./components/UnifiedShell/PluginChatToUnifiedShellRedirect').then((m) => ({
    default: m.PluginChatToUnifiedShellRedirect,
  })),
);

// Plugin route guard and pages (Story PU-1.7)
const PluginRouteGuard = lazy(() =>
  import('./components/features/plugin/PluginRouteGuard').then((m) => ({
    default: m.PluginRouteGuard,
  }))
);
const PluginItemDetailPage = lazy(() => import('./pages/plugin/PluginItemDetailPage'));

// Admin pages
const PromptsListPage = lazy(() => import('./pages/admin/PromptsListPage'));
const PromptEditPage = lazy(() => import('./pages/admin/PromptEditPage'));
const PromptHistoryPage = lazy(() => import('./pages/admin/PromptHistoryPage'));
const AgentsListPage = lazy(() => import('./pages/admin/AgentsListPage'));
const AgentDetailPage = lazy(() => import('./pages/admin/AgentDetailPage'));
const ScheduledJobRunsPage = lazy(() => import('./pages/admin/ScheduledJobRunsPage'));
const PerTierByteCostPage = lazy(() => import('./pages/admin/PerTierByteCostPage'));
const UsageLlmCostPage = lazy(() => import('./pages/admin/UsageLlmCostPage'));
const BillingCreditsPage = lazy(() => import('./pages/admin/BillingCreditsPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/billing/CheckoutSuccessPage'));
const TenantsAdminPage = lazy(() => import('./pages/admin/TenantsAdminPage'));
const TenantDetailAdminPage = lazy(() => import('./pages/admin/TenantDetailAdminPage'));

// Error pages (keep small, load eagerly for reliability)
import NotFoundPage from './pages/NotFoundPage';

// Legacy route redirects (Story PU-6.8)
import {
  LegacyAgentChatRedirect,
  LegacyCampaignDetailRedirect,
  LegacyMarketingRedirects,
} from './routes/LegacyRedirects';

// Per-plugin calendar/board URLs redirect to the unified /calendar page (T-20260915-1);
// per-plugin dashboard URLs redirect to /dashboards (T-20260915-2);
// per-plugin settings URLs redirect to /settings/plugins (T-20260915-3)
import {
  PluginCalendarRedirect,
  PluginDashboardRedirect,
  PluginKanbanRedirect,
  PluginSettingsRedirect,
  PluginLibraryRedirect,
} from './routes/PluginViewRedirects';

import { makeQueryClient } from './lib/query/queryClient';

// Legacy item detail redirect (Story PU-7.15)
import { LegacyItemDetailRedirect } from './components/features/redirects/LegacyItemDetailRedirect';

// Story 30.1 — routes a tapped push into an already-open window
import { PushNavigationListener } from './lib/push/navigateListener';

// Create React Query client.
//
// The defaults live in `lib/query/queryClient.ts` so a test can hold them —
// `App` mounts the whole router, so nothing in the suite can construct it, and
// `retry` / `refetchOnWindowFocus` are behaviour (T-20260826-1), not taste.
// `queryClient.test.ts` fails if this line stops using the shared factory.
const queryClient = makeQueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Announcer />
      <Toaster />
      <BrowserRouter>
        {/* Story 30.1 — beside <Routes>, not in MainLayout: a tap must reach a window on /login or a 404 too */}
        <PushNavigationListener />
        <AuthProvider>
          <Suspense fallback={<PageLoadingFallback fullViewport />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupRouter />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected routes with MainLayout */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* Dashboards board switcher (T-20260915-2 shell; boards since Epic 8
                    story 8.3); the old /start and /dashboard landings redirect to it */}
                <Route path="/dashboards" element={<DashboardsPage />} />
                <Route path="/dashboards/:boardId" element={<DashboardsPage />} />
                <Route path="/start" element={<Navigate to="/dashboards" replace />} />
                <Route path="/dashboard" element={<Navigate to="/dashboards" replace />} />
                {/* Story 13.1 — the old invite-accept target never had a page; first
                    run now happens in the Chief of Staff's chat. A stray link lands home. */}
                <Route path="/onboarding" element={<Navigate to="/dashboards" replace />} />
                <Route path="/library" element={<DocumentLibraryPage />} />
                <Route path="/library/:documentId" element={<DocumentDetailPage />} />
                {/* Legacy N8N workflow-chat routes (deleted, T-20260919-4) —
                    stale bookmarks land on the dashboards page */}
                <Route path="/workflows" element={<Navigate to="/dashboards" replace />} />
                <Route path="/workflow/*" element={<Navigate to="/dashboards" replace />} />
                {/* Unified cross-plugin Calendar + Board page (T-20260915-1) */}
                <Route path="/calendar" element={<CalendarPage />} />
                {/* Legacy /calendar/item/:id/draft routes redirect to plugin item detail (PU-7.15) */}
                <Route path="/calendar/item/:id/draft" element={<LegacyItemDetailRedirect />} />
                {/* Legacy /campaigns routes redirect to plugin library (PU-6.8) */}
                <Route path="/campaigns" element={<LegacyMarketingRedirects.Campaigns />} />
                <Route path="/campaigns/:id" element={<LegacyCampaignDetailRedirect />} />
                {/* T-20260916-2 — every Admin row is owner-only, route included.
                    The nav flag only hides; <RequireOwner> is the gate, and the
                    page self-gates behind it (belt-and-braces). */}
                <Route path="/import/csv" element={<RequireOwner><CSVImportPage /></RequireOwner>} />
                {/* Legacy analytics pages (deleted, T-20260919-7a) —
                    stale bookmarks land on the dashboards page */}
                <Route path="/performance" element={<Navigate to="/dashboards" replace />} />
                <Route path="/monthly-reviews" element={<Navigate to="/dashboards" replace />} />
                {/* Story 24.8 — the Settings hub: role-open (ruled M-2), no guard;
                    it filters its own cards by role from adminNavigation. */}
                <Route path="/settings" element={<SettingsHubPage />} />
                {/* T-20260916-2 — owner-only (team management); moved from
                    /settings by Story 24.8. */}
                <Route path="/settings/team" element={<RequireOwner><SettingsPage /></RequireOwner>} />
                <Route path="/settings/scheduled-tasks" element={<ScheduledTasksSettingsPage />} />
                {/* Story 4.2 — platform-level integration credentials (owner-gated in the page). */}
                <Route path="/settings/integrations" element={<IntegrationsSettingsPage />} />
                {/* T-20260916-8 — API keys. <RequireOwner> here, NOT the
                    page-self-gate-only shape its two neighbours above still
                    carry: those predate T-20260916-2 and are documented debt on
                    `adminNavigation.routes.test.ts`'s allowlist, which only
                    shrinks. The page then narrows again to `isOwnerByRole()`,
                    because this guard admits a superuser member whose every
                    call to `api_keys.manage` would 403. */}
                <Route path="/settings/api-keys" element={<RequireOwner><ApiKeysSettingsPage /></RequireOwner>} />
                {/* Story 29.3 — Settings › Notifications: role-open BY DESIGN, no
                    guard. Members and viewers see the contacts read-only
                    (canEdit from the harness); every write is owner-gated at
                    the harness (createRequireOwnerRole). */}
                <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                {/* T-20260915-3 — per-plugin settings with a plugin switcher; the old
                    /plugins/:pluginId/settings URLs redirect here */}
                {/* T-20260916-2 — owner-only (enable/disable + the settings form). */}
                <Route path="/settings/plugins" element={<RequireOwner><PluginSettingsHostPage /></RequireOwner>} />

                {/* Legacy /marketing/* routes redirect to plugin routes (PU-6.8) */}
                <Route path="/marketing" element={<LegacyMarketingRedirects.MarketingRoot />} />
                <Route path="/marketing/chat" element={<LegacyMarketingRedirects.MarketingChat />} />
                <Route path="/marketing/chat/:agentId" element={<LegacyAgentChatRedirect />} />
                <Route path="/marketing/calendar" element={<LegacyMarketingRedirects.MarketingCalendar />} />
                <Route path="/marketing/campaigns" element={<LegacyMarketingRedirects.MarketingCampaigns />} />
                <Route path="/marketing/settings" element={<LegacyMarketingRedirects.MarketingSettings />} />
                <Route path="/content-calendar" element={<LegacyMarketingRedirects.ContentCalendar />} />

                {/* Plugin routes */}
                <Route path="/marketplace" element={<MarketplacePage />} />

                {/* In-app documentation — owner-facing guide */}
                <Route path="/docs/*" element={<DocsPage />} />

                {/* Story 24.7 — phone navigation. `/more` is role-open (it
                    renders only the rows the viewer may open); `/engineering`
                    lists the four Engineering pages, superuser only. */}
                <Route path="/more" element={<MorePage />} />
                <Route path="/engineering" element={<RequireSuperuser><EngineeringPage /></RequireSuperuser>} />

                {/* Unified chat shell (Story 18.7) — must precede /plugins/:pluginId so /chat matches first */}
                <Route path="/chat" element={<ChatPage />} />

                {/* Dedicated task surface (Epic 6 story 6.1). `/chat?view=my-tasks`
                    redirects here from ChatPage, preserving its filter params. */}
                <Route path="/tasks" element={<TasksPage />} />
                {/* ADR-022 S3/S4 — the ONE review route. Mounts the page
                    `review-surfaces.ts` registers for the ticket's task_type,
                    else replaces itself with `/tasks?taskId=`. */}
                <Route path="/tasks/:taskId/review" element={<TaskReviewPage />} />

                {/* Plugin page routes (Story PU-1.7) */}
                <Route path="/plugins/:pluginId" element={<PluginRouteGuard />}>
                  <Route index element={<Navigate to="chat" replace />} />
                  <Route path="chat" element={<PluginChatToUnifiedShellRedirect />} />
                  {/* T-20260915-2 — dashboards live on /dashboards; query string preserved */}
                  <Route path="dashboard" element={<PluginDashboardRedirect />} />
                  {/* T-20260915-4 — the per-plugin Library is gone; /library filtered to the plugin */}
                  <Route path="library" element={<PluginLibraryRedirect />} />
                  {/* T-20260915-1 — both views live on /calendar; query string preserved */}
                  <Route path="kanban" element={<PluginKanbanRedirect />} />
                  <Route path="calendar" element={<PluginCalendarRedirect />} />
                  <Route path="item/:itemId" element={<PluginItemDetailPage />} />
                  {/* T-20260915-3 — settings live on /settings/plugins; query string preserved */}
                  <Route path="settings" element={<PluginSettingsRedirect />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* Admin routes - Superuser only (Story PPA-2.1, PPA-2.2) */}
                <Route path="/admin/prompts" element={<RequireSuperuser><PromptsListPage /></RequireSuperuser>} />
                {/* Platform prompt routes (AC-6) */}
                <Route path="/admin/prompts/platform/edit" element={<RequireSuperuser><PromptEditPage /></RequireSuperuser>} />
                <Route path="/admin/prompts/platform/history" element={<RequireSuperuser><PromptHistoryPage /></RequireSuperuser>} />
                {/* Agent prompt routes (AC-6) */}
                <Route path="/admin/prompts/:pluginId/agent/:agentId/edit" element={<RequireSuperuser><PromptEditPage /></RequireSuperuser>} />
                <Route path="/admin/prompts/:pluginId/agent/:agentId/history" element={<RequireSuperuser><PromptHistoryPage /></RequireSuperuser>} />

                {/* Agents admin — role-open by design (Story 14.6, the /marketplace
                    precedent): members and viewers see the tenant's agents and
                    specialists read-only; the Archive control and the skills
                    section are owner-gated inside the page, and the archive and
                    skills routes stay owner-gated server-side. Pinned as
                    `ROLE_OPEN_BY_DESIGN` in adminNavigation.routes.test.ts. */}
                <Route path="/admin/agents" element={<AgentsListPage />} />
                <Route path="/admin/agents/:agentId" element={<AgentDetailPage />} />
                {/* Scheduled-jobs run-history admin — Owner only (Story 20.4) */}
                <Route path="/admin/scheduled-jobs/:id/runs" element={<RequireOwner><ScheduledJobRunsPage /></RequireOwner>} />
                {/* 🔴 NO REDIRECT FOR THE RETIRED `/admin/observability/manifest-emission-rate`,
                    and that is a decision, not an omission (T-20260916-6). The
                    legacy `<Navigate>` rows above (`/dashboard`, `/campaigns`,
                    `/marketing/*`) all point a surface that MOVED at where it
                    moved to. This one did not move — it was retired, and there
                    is no replacement page. Redirecting a bookmark to Byte Cost
                    or Usage & LLM Cost would answer a question the user did not
                    ask with a different metric, silently; and redirecting to
                    /dashboards would claim the URL still means something. A
                    bookmark to a page that no longer exists SHOULD land on
                    NotFoundPage — that is the honest answer, and the one that
                    makes the retirement discoverable rather than confusing.
                    Revisit only if the metric ever gets a surface again. */}
                {/* 🔴 AND NO REDIRECT FOR THE RETIRED `/admin/agent-tasks/audit`
                    either (T-20260916-7), for the same reason and recorded here
                    so the two retirements do not read as decided differently.
                    The Task Audit page was a five-axis filter bar, a 500-row cap
                    and a CSV export over 231 rows — retired for SCALE, not
                    redundancy. Nothing replaced it: `originator` is not shown
                    anywhere in the task components, so /tasks is NOT where that
                    bookmark should land. It falls through to `path="*"` →
                    NotFoundPage, which is the honest answer. The
                    `agent_task_audit` ledger, its constraint trigger and all
                    three writers are untouched and keep accruing. */}
                {/* Per-tier byte cost dashboard — SUPERUSER only since T-20260916-3
                    (Story 24.2 shipped it owner-gated). The reason is Paul's ruling
                    that per-turn context byte cost is a platform ENGINEERING metric —
                    it measures how the harness assembles prompts, which is our
                    business and not the tenant's — so the nav row moved under the
                    SuperAdmin submenu and the gate moved with it.
                    🔴 NOT "because it aggregates cross-tenant". An earlier draft of
                    this comment said that, and it is false as a discriminator: the
                    rule is whose CONCERN the number is. Per-turn context byte cost
                    measures how WE assemble prompts, so it is ours; `usage-llm-cost`
                    directly below reports a tenant's own spend, so it is theirs and
                    stays owner-gated.
                    🔴 THE COUNTER-EXAMPLE THAT REFUTED THE AGGREGATION RULE IS GONE,
                    AND NOTHING REPLACES IT. It was `manifest-emission-rate`:
                    cross-tenant AND deliberately owner-visible, so it disproved the
                    rule by existing. T-20260916-6 retired that page. Byte Cost is
                    now the ONLY cross-tenant route in this family — `usage-llm-cost`
                    filters on the JWT-derived tenantId — so the aggregation rule
                    happens to fit a surviving set of one. Do not read that
                    coincidence as the rule, and do not go looking for a live
                    counter-example here: there is not one.
                    The gate had to move WITH the row — a nav flag only hides, so
                    leaving <RequireOwner> here would have kept the URL open to every
                    tenant owner while the menu said otherwise. The API half is
                    `createRequireSuperuser` on this endpoint's handler in
                    `routes/admin-observability.ts`.
                    🔴 THE DENIAL EXPERIENCE CHANGED, and it was accepted (review
                    P16). <RequireOwner> bounces silently to /start and says nothing
                    — FR20 + UX-DR7, deliberately, so a refused user is not told the
                    surface exists. <RequireSuperuser> renders an "Access Denied"
                    card instead. This is the FIRST route to cross from one guard to
                    the other, so it is the first place the two UXes are visibly
                    inconsistent. Accepted because the audience is a handful of
                    tenant owners who had the row yesterday and would otherwise get
                    an unexplained bounce; pinned by
                    `features/auth/__tests__/RequireSuperuser.test.tsx`. Reconciling
                    the two guards' denial UX is a separate decision, not a
                    consistency cleanup to be done in passing. */}
                <Route path="/admin/observability/per-tier-byte-cost" element={<RequireSuperuser><PerTierByteCostPage /></RequireSuperuser>} />
                {/* Usage & LLM cost dashboard — Owner only */}
                <Route path="/admin/observability/usage-llm-cost" element={<RequireOwner><UsageLlmCostPage /></RequireOwner>} />
                {/* Story 16.7 — Stripe Checkout return page (polls, never credits).
                    <RequireOwner> admits a superuser; the page narrows to
                    isOwnerByRole() because the status route is role-pure. */}
                <Route path="/billing/checkout/success" element={<RequireOwner><CheckoutSuccessPage /></RequireOwner>} />
                {/* Billing & credits management — Superuser only (Tenant Credit Balance, Pass 2) */}
                <Route path="/admin/billing" element={<RequireSuperuser><BillingCreditsPage /></RequireSuperuser>} />
                {/* Tenant admin (organisation kind) — Superuser only (Story 10.4) */}
                <Route path="/admin/tenants" element={<RequireSuperuser><TenantsAdminPage /></RequireSuperuser>} />
                {/* Story 11.2 — superuser tenant detail: private-plugin grants. */}
                <Route path="/admin/tenants/:tenantId" element={<RequireSuperuser><TenantDetailAdminPage /></RequireSuperuser>} />
              </Route>

              <Route path="*" element={<NotFoundPage fullViewport />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
